import { Component, OnInit } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import {
  Component as SystemManagerComponent,
  ComponentTypes,
  MiccbApiService,
} from "@mitel-internal/miccb-components";
import { Observable, Subject } from "rxjs";
import { catchError, finalize, takeUntil, tap } from "rxjs/operators";
import { AccountsService } from "src/app/core/services/accounts.service";
import { AdminService } from "src/app/core/services/admin.service";
import { SpinnerService } from "src/app/core/services/spinner.service";

const logContext = 'MiccConfigDialogComponent';
@Component({
  selector: "app-micc-config-dialog",
  templateUrl: "./micc-config-dialog.component.html",
  styleUrls: ["./micc-config-dialog.component.scss"],
})
export class MiccConfigDialogComponent implements OnInit {
  miccComponents$: Observable<SystemManagerComponent[]>;
  onDestroy$ = new Subject();
  activeComponent: SystemManagerComponent;
  originalTag: any;
  accountId: string;
  loadingErrorMessage: string | undefined;
  loading = true;

  constructor(
    public dialogRef: MatDialogRef<MiccConfigDialogComponent>,
    private adminService: AdminService,
    private accountsService: AccountsService,
    private miccbApiService: MiccbApiService,
    private spinnerService: SpinnerService
  ) { }

  ngOnInit(): void {
    this.accountId = this.accountsService.getCurrentAccount().accountId;
    this.setupComponentList();
    this.setupActiveComponent();
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    this.loadingErrorMessage = undefined;
    if (!!this.activeComponent?.componentId && this.activeComponent?.componentId != this.originalTag?.active?.componentId) {
      // Active MiCC-B component has changed. Save it in account tags
      console.log(`${logContext}.onSave updating active=${this.activeComponent?.name} ${this.activeComponent?.componentId}`);
      const tag = this.miccbApiService.createTagObject(this.originalTag, this.activeComponent);
      this.spinnerService.setSpinnerMessageByKey('spinnerMessages.please-wait-saving');
      const delayedSpinner = setTimeout(() => this.spinnerService.showSpinner(), 750);
      this.adminService.updateAccountTag(this.accountId, MiccbApiService.miccbComponentTagId, tag)
        .catch(err => {
          console.warn(`${logContext}.onSave failed`, err);
          this.loadingErrorMessage = 'common.messages.ERROR_DEFAULT'
        })
        .finally(() => {
          this.miccbApiService.flushActiveMiccbCache(this.accountId);
          clearTimeout(delayedSpinner);
          this.spinnerService.hideSpinner();
          this.dialogRef.close();
        });
    } else {
      this.dialogRef.close();
    }
  }

  setupComponentList() {
    this.loading = true;
    this.loadingErrorMessage = undefined;
    this.spinnerService.setSpinnerMessageByKey('spinnerMessages.loading_wait');
    // only show the spinner when it looks like we are struggling to load the data
    const delayedSpinner = setTimeout(() => this.spinnerService.showSpinner(), 750);
    return this.miccComponents$ = this.miccbApiService.getMiccbComponents()
    .pipe(
      tap(components => {
        if (components.length === 1) {
          this.activeComponent = components[0];
        } else if (components.length === 0) {
          this.loadingErrorMessage = 'integrations.micc-config.status.no-micc-found';
        }
      }),
      catchError(err => {
        console.warn(`${logContext}.setupComponentList error`, err);
        this.loadingErrorMessage = 'users_list.errorLoadingMiccbInfo';
        return [];
      }),
      finalize(() => {
        clearTimeout(delayedSpinner);
        this.spinnerService.hideSpinner();
        this.loading = false;
      }),
      takeUntil(this.onDestroy$)
    );
  }

  setupActiveComponent() {
    return this.adminService.getAccountTagsNoFrills(this.accountId)
    .then(tags => tags[MiccbApiService.miccbComponentTagId])
    .then(tag => this.originalTag = tag)
    .then(tag => {
      if (tag?.active?.componentId) {
        this.activeComponent = {
          accountId: this.accountId,
          componentType: ComponentTypes.MiccB,
          componentId: tag.active.componentId,
          name: tag.active.name
        }
      }
    });
  }
}
